import { Masonry } from "masonic";
import { MarketingCarouselCategories, PanelLink } from "../../components/MarketingCarouselCategories";
import { MarketingFooter } from "../../components/MarketingFooter";
import { MarketingMenu } from "../../components/MarketingMenu";
import { MarketingOGTags } from "../../components/MarketingOGTags";
import { FadeUpContainer } from "../../components/ReusableStyledComponents/FadeUp";
import { UnstyledLink } from "../../components/ReusableStyledComponents/UnstyledLink";
import { V3EmbedProfile } from "../../components/V3EmbedProfile";
import { VerticalSpacing } from "../../components/VerticalSpacing";
import {
    EmbedMasonCardWrapper,
    EmbedProfileWrapper,
    MarketingLinksGallery,
    ExampleMidPageTextWrapper,
    FixedLinkWrapper,
    ExampleLayoutDescription
} from "./ExamplesPageLayout.styles";
import { theme } from "../../theme/theme";
import { Body, H2 } from "../../components/Typography";
import { ExampleUserListResponse, UserResponse } from "@authory-packages/core";
import { SEO_URL_LANDING_PAGE_PREFIX, SEO_URL_EXAMPLE_PAGES_SUFIX } from "@authory-packages/seo-static-data";
import { EmbedMode, EmbedTheme } from "../../utils/embeds";
import Head from "next/head";
import { APPLICATION_ROUTES } from "../../shared-logic/routes";
import { MarketingCarouselCategorieCard } from "../../components/MarketingCarouselCategories/MarketingCarouselCategories.styles";
import { V3BodyGrey35 } from "../../components/ReusableStyledComponents/V3BodyGrey35";
import { useEffect, useState, type JSX } from "react";
import { BlogPagination } from "../../components/BlogPagination";
import { Pagination } from "@tryghost/content-api";
import { MarketingCTALinkCard } from "../../components/ReusableStyledComponents/MarketingCTALinkCard";
import { V3ParagraphGrey35 } from "../../components/ReusableStyledComponents/V3ParagraphGrey35";
import { MarketingTitle } from "../../components/ReusableStyledComponents/MarketingMainTitle";
import { MarketingFAQ } from "../../components/MarketingFAQ";
import { generateFAQSchema } from "../../utils/faqSchemaGenerator";
import { InlineLink } from "../../components/ReusableStyledComponents/InlineLink";

export type EmbedConf = {
    embedMode: EmbedMode;
    showSocialMedia: boolean;
    showHighlight: boolean;
    items: number;
};

interface ExamplesPageLayoutProps {
    title: JSX.Element;
    description: JSX.Element;
    panelLinks: PanelLink[];
    categoriesLinks: PanelLink[];
    masonryItems: ExampleUserListResponse;
    masonryMaxWidth?: string;
    midPageTitle?: string;
    ogTitle?: string;
    ogDescription?: string;
    urlKey?: string;
    faq: {
        question: string;
        answer: string;
    }[];
    embedConf: EmbedConf[];
    pagination?: Pagination;
    paginationURL?: string;
    skipCarousel?: boolean;
    canonicalUrl?: string;
    linkToUseCase?: boolean;
}

export const DOMAINS_SEO = [
    {
        url: `${APPLICATION_ROUTES.EXAMPLES_PROFESSION}`,
        label: "By profession"
    },
    {
        url: `${APPLICATION_ROUTES.EXAMPLES_CATEGORIES}`,
        label: "By categories"
    },
    {
        url: `${APPLICATION_ROUTES.EXAMPLES_PROFESSION_CATEGORIES}`,
        label: "By professional categories"
    }
];

const primaryCatLinks = [
    "artist",
    "writer",
    "copywriter",
    "photographer",
    "content-creator",
    "digital-marketer",
    "illustrator",
    "designer",
    "video-editor",
    "content-writer",
    "freelancer",
    "freelance-writer",
    "social-media-manager",
    "content-marketing",
    "journalist",
    "editor",
    "thought-leader",
    "social-media-marketer",
    "content-marketer",
    "creative-writer",
    "music",
    "tech",
    "technology",
    "politics",
    "environment",
    "travel",
    "lifestyle",
    "celebrity",
    "finance",
    "food",
    "health",
    "education",
    "entertainment",
    "celebrity",
    "crime",
    "business",
    "fashion",
    "science",
    "weather",
    "games",
    "international",
    "social-media",
    "film",
    "movie",
    "literature",
    "news",
    "history",
    "culture",
    "art",
    "investigative",
    "business-analyst",
    "fashion-designer",
    "news-reporter",
    "fashion-blogger",
    "news-anchor",
    "travel-blogger",
    "music-producer",
    "fashion-poet",
    "music-editor",
    "social-media-influencer",
    "film-producer",
    "lifestyle-blogger",
    "food-critic",
    "weather-reporter",
    "music-composer",
    "crime-analyst",
    "movie-critic",
    "movie-producer",
    "music-artist",
    "movie-editor",
    "social-media-content-creator",
    "music-creator",
    "art-creator",
    "film-editor",
    "finance-analyst",
    "food-photographer",
    "travel-photographer",
    "music-speaker",
    "travel-writer",
    "food-blogger",
    "film-critic",
    "news-producer",
    "environment-artist",
    "investigative-journalist",
    "tech-writer",
    "movie-creator",
    "social-media expert",
    "investigative-analyst",
    "environment-analyst",
    "food-influencer"
];

export const ExamplesPageLayout = ({
    title,
    description,
    panelLinks,
    masonryItems,
    categoriesLinks,
    masonryMaxWidth,
    midPageTitle,
    ogTitle = "",
    ogDescription = "",
    faq,
    urlKey,
    embedConf,
    pagination,
    skipCarousel = false,
    paginationURL,
    canonicalUrl,
    linkToUseCase = false
}: ExamplesPageLayoutProps) => {
    const [useSSR, setUseSSR] = useState(false);

    const embedList = masonryItems.users.map((item, index) => {
        return {
            embedTheme: EmbedTheme.light,
            embedMode: embedConf[index].embedMode,
            showSocialMedia: embedConf[index].showSocialMedia,
            showHighlight: embedConf[index].showHighlight,
            useCover: true,
            items: [],
            author: {
                ...(item.user as UserResponse)
            }
        };
    });

    const fixedLinks = categoriesLinks.filter((cat) =>
        primaryCatLinks.map((it) => `${it}${SEO_URL_EXAMPLE_PAGES_SUFIX}`).includes(cat.url.replace("/examples/", ""))
    );
    const carouselLinks = categoriesLinks
        .filter((cat) => !primaryCatLinks.map((it) => `${it}${SEO_URL_EXAMPLE_PAGES_SUFIX}`).includes(cat.url.replace("/examples/", "")))
        .slice(0, 30);

    useEffect(() => {
        setUseSSR(true);
    }, []);

    let jsonLd = "";

    if (faq) {
        jsonLd = generateFAQSchema(faq);
    }

    return (
        <>
            {!!urlKey && (
                <Head>
                    <link
                        rel="canonical"
                        href={`${process.env.NEXT_PUBLIC_FRONTEND_URL}${APPLICATION_ROUTES.EXAMPLES}/${urlKey}${SEO_URL_EXAMPLE_PAGES_SUFIX}`}
                    />
                    {!!jsonLd.length && <script type="application/ld+json">{jsonLd}</script>}
                </Head>
            )}
            <MarketingOGTags title={ogTitle} description={ogDescription} canonicalUrl={canonicalUrl} />
            <MarketingMenu useNextRouter={false} />
            <FadeUpContainer>
                <VerticalSpacing top={20} breakpoints={[{ screen: theme.breakpoints.tablet, marginTop: 50 }]}>
                    <ExampleLayoutDescription>
                        <VerticalSpacing top={20} breakpoints={[{ screen: theme.breakpoints.tablet, marginTop: 50 }]}>
                            <MarketingTitle as="h1">{title}</MarketingTitle>
                            {description && <Body>{description}</Body>}
                            {linkToUseCase && (
                                <VerticalSpacing top={30}>
                                    <Body>
                                        Click on any name to see their portfolio in full, or{" "}
                                        <InlineLink
                                            href={`${process.env.NEXT_PUBLIC_FRONTEND_URL}${APPLICATION_ROUTES.USE_CASES}/${SEO_URL_LANDING_PAGE_PREFIX}${urlKey}`}
                                        >
                                            learn more about creating an {urlKey} portfolio
                                        </InlineLink>
                                        .
                                    </Body>
                                </VerticalSpacing>
                            )}
                        </VerticalSpacing>
                    </ExampleLayoutDescription>
                </VerticalSpacing>
                {!!panelLinks.length && (
                    <VerticalSpacing top={80}>
                        <MarketingLinksGallery>
                            {panelLinks.map((cat) => (
                                <div key={cat.url}>
                                    <UnstyledLink href={cat.url}>
                                        <MarketingCTALinkCard>
                                            <V3ParagraphGrey35>{cat.label}</V3ParagraphGrey35>
                                        </MarketingCTALinkCard>
                                    </UnstyledLink>
                                </div>
                            ))}
                        </MarketingLinksGallery>
                    </VerticalSpacing>
                )}
                {!!midPageTitle && (
                    <VerticalSpacing top={80}>
                        <ExampleMidPageTextWrapper>
                            <H2>{midPageTitle}</H2>
                        </ExampleMidPageTextWrapper>
                    </VerticalSpacing>
                )}
                <VerticalSpacing top={40}>
                    <EmbedProfileWrapper $maxWidth={masonryMaxWidth}>
                        {useSSR && (
                            <Masonry
                                items={embedList}
                                render={({ index, data }) => (
                                    <EmbedMasonCardWrapper key={index}>
                                        <V3EmbedProfile
                                            author={data.author}
                                            embedMode={data.embedMode}
                                            embedTheme={data.embedTheme}
                                            items={data.items}
                                            showSocialMedia={data.showSocialMedia}
                                            showHighlight={data.showHighlight}
                                            useCover={data.useCover}
                                            numBioLines={4}
                                        />
                                    </EmbedMasonCardWrapper>
                                )}
                                columnGutter={20}
                                columnWidth={280}
                            />
                        )}
                    </EmbedProfileWrapper>
                </VerticalSpacing>
                {!skipCarousel && !!carouselLinks.length && (
                    <VerticalSpacing top={80}>
                        <MarketingCarouselCategories categoriesLinks={carouselLinks} />
                    </VerticalSpacing>
                )}
                {!!fixedLinks.length && (
                    <VerticalSpacing top={30}>
                        <FixedLinkWrapper>
                            {fixedLinks.map((link) => (
                                <UnstyledLink href={link.url} key={link.url}>
                                    <MarketingCarouselCategorieCard>
                                        <V3BodyGrey35>{link.label}</V3BodyGrey35>
                                    </MarketingCarouselCategorieCard>
                                </UnstyledLink>
                            ))}
                        </FixedLinkWrapper>
                    </VerticalSpacing>
                )}
                {!!pagination && <BlogPagination pagination={pagination} url={paginationURL} />}
                <VerticalSpacing top={30}>
                    <FixedLinkWrapper>
                        {!!categoriesLinks.length &&
                            DOMAINS_SEO.map((domain) => (
                                <UnstyledLink href={domain.url} key={domain.url}>
                                    <MarketingCarouselCategorieCard>
                                        <V3BodyGrey35>Portfolio examples {domain.label.replace("By", "by")}</V3BodyGrey35>
                                    </MarketingCarouselCategorieCard>
                                </UnstyledLink>
                            ))}
                    </FixedLinkWrapper>
                </VerticalSpacing>
                {faq && !!faq.length && (
                    <VerticalSpacing top={120} bottom={30}>
                        <MarketingFAQ questions={faq} />
                    </VerticalSpacing>
                )}
                <MarketingFooter useNextRouter={false} />
            </FadeUpContainer>
        </>
    );
};
